export const AUTH_SYNC_CHANNEL_NAME = 'evergreen-auth-sync';
export const AUTH_SESSION_UPDATED_EVENT = 'evergreen-auth-session-updated';
export const AUTH_SESSION_CLEARED_EVENT = 'evergreen-auth-session-cleared';

export interface SessionUserInfo extends Record<string, unknown> {
	userNumber?: number | null;
	username?: string | null;
	firstName?: string | null;
	middleName?: string | null;
	lastName?: string | null;
	role?: string | null;
	city?: string | null;
	state?: string | null;
	groupId?: number | null;
}

export interface ImpersonationState extends Record<string, unknown> {
	isImpersonating?: boolean;
	impersonatedUser?: Record<string, unknown> | null;
	impersonationGroupId?: number | null;
	impersonationLogId?: number | null;
	originalToken?: string | null;
	originalUserInfo?: Record<string, unknown> | null;
	isSignupImpersonation?: boolean;
	signupSessionToken?: string | null;
}

export interface AuthSessionSnapshot {
	authToken: string;
	refreshToken: string | null;
	userInfo: SessionUserInfo;
	impersonationState: ImpersonationState | null;
}

export type AuthSessionEventSource = 'local' | 'remote';

export interface AuthSessionEventDetail {
	source: AuthSessionEventSource;
}

export type AuthSyncMessage =
	| {
			type: 'request-session';
			requestId: string;
			sourceTabId: string;
	  }
	| {
			type: 'session-response';
			requestId: string;
			snapshot: AuthSessionSnapshot;
			sourceTabId: string;
	  }
	| {
			type: 'session-updated';
			snapshot: AuthSessionSnapshot;
			sourceTabId: string;
	  }
	| {
			type: 'session-cleared';
			sourceTabId: string;
	  };

const SESSION_STORAGE_KEYS = {
	authToken: 'auth_token',
	refreshToken: 'refresh_token',
	userInfo: 'user_info',
	impersonationState: 'impersonation_state'
} as const;

const LOCAL_STORAGE_AUTH_KEYS = ['user_groups', 'user_groups_user_number', 'user_roles', 'user_settings', 'assignor_clients', 'group_membership', 'clients', 'original_user_roles', 'original_user_groups', 'original_user_groups_user_number'] as const;

const CURRENT_TAB_ID = createTabId();

let authSyncChannel: BroadcastChannel | null = null;

if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
	authSyncChannel = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
}

export function getAuthSyncChannel(): BroadcastChannel | null {
	return authSyncChannel;
}

export function getCurrentTabId(): string {
	return CURRENT_TAB_ID;
}

export function postAuthSyncMessage(message: AuthSyncMessage): void {
	authSyncChannel?.postMessage(message);
}

export function parseStoredJson<T>(rawValue: string | null, fallback: T): T {
	if (!rawValue) {
		return fallback;
	}

	try {
		return JSON.parse(rawValue) as T;
	} catch {
		return fallback;
	}
}

export function createTabId(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}

	return `tab-${String(Date.now())}-${Math.random().toString(36).slice(2)}`;
}

export function readAuthSessionSnapshot(): AuthSessionSnapshot | null {
	const authToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.authToken);
	const userInfo = parseStoredJson<SessionUserInfo | null>(sessionStorage.getItem(SESSION_STORAGE_KEYS.userInfo), null);

	if (!authToken || !userInfo) {
		return null;
	}

	return {
		authToken,
		refreshToken: sessionStorage.getItem(SESSION_STORAGE_KEYS.refreshToken),
		userInfo,
		impersonationState: parseStoredJson<ImpersonationState | null>(sessionStorage.getItem(SESSION_STORAGE_KEYS.impersonationState), null)
	};
}

export function persistAuthSessionSnapshot(snapshot: AuthSessionSnapshot): void {
	sessionStorage.setItem(SESSION_STORAGE_KEYS.authToken, snapshot.authToken);
	sessionStorage.setItem(SESSION_STORAGE_KEYS.userInfo, JSON.stringify(snapshot.userInfo));

	if (snapshot.refreshToken) {
		sessionStorage.setItem(SESSION_STORAGE_KEYS.refreshToken, snapshot.refreshToken);
	} else {
		sessionStorage.removeItem(SESSION_STORAGE_KEYS.refreshToken);
	}

	if (snapshot.impersonationState) {
		sessionStorage.setItem(SESSION_STORAGE_KEYS.impersonationState, JSON.stringify(snapshot.impersonationState));
	} else {
		sessionStorage.removeItem(SESSION_STORAGE_KEYS.impersonationState);
	}
}

export function updateSessionTokens(accessToken: string, refreshToken: string | null): void {
	sessionStorage.setItem(SESSION_STORAGE_KEYS.authToken, accessToken);
	if (refreshToken) {
		sessionStorage.setItem(SESSION_STORAGE_KEYS.refreshToken, refreshToken);
	} else {
		sessionStorage.removeItem(SESSION_STORAGE_KEYS.refreshToken);
	}
}

export function hasPersistedAuthSession(): boolean {
	return readAuthSessionSnapshot() !== null;
}

export function clearPersistedAuthArtifacts(): void {
	sessionStorage.removeItem(SESSION_STORAGE_KEYS.authToken);
	sessionStorage.removeItem(SESSION_STORAGE_KEYS.refreshToken);
	sessionStorage.removeItem(SESSION_STORAGE_KEYS.userInfo);
	sessionStorage.removeItem(SESSION_STORAGE_KEYS.impersonationState);

	for (const key of LOCAL_STORAGE_AUTH_KEYS) {
		localStorage.removeItem(key);
	}
}

export function emitAuthSessionUpdated(source: AuthSessionEventSource): void {
	window.dispatchEvent(
		new CustomEvent<AuthSessionEventDetail>(AUTH_SESSION_UPDATED_EVENT, {
			detail: { source }
		})
	);
}

export function emitAuthSessionCleared(source: AuthSessionEventSource): void {
	window.dispatchEvent(
		new CustomEvent<AuthSessionEventDetail>(AUTH_SESSION_CLEARED_EVENT, {
			detail: { source }
		})
	);
}

export function broadcastSessionUpdated(): void {
	const snapshot = readAuthSessionSnapshot();
	if (!snapshot) {
		return;
	}

	postAuthSyncMessage({
		type: 'session-updated',
		snapshot,
		sourceTabId: CURRENT_TAB_ID
	});
}

export function broadcastSessionCleared(): void {
	postAuthSyncMessage({
		type: 'session-cleared',
		sourceTabId: CURRENT_TAB_ID
	});
}
